export interface ShellUrlConfig {
    readonly userId: string;
    readonly streamGroupId: string;
    readonly applicationId: string;
    readonly location: string;
    readonly showStats: boolean;
    readonly streamStartupTimeoutMs: number | null;
    readonly controllerId: string;
    readonly hasControllerId: boolean;
    readonly hasRequiredStreamParams: boolean;
    readonly publisherId: string;
    readonly publisherProductId: string;
}

const DEFAULT_LOCATION = 'us-east-2';
const DEFAULT_USER_ID = 'Player1';
const MIN_STREAM_STARTUP_TIMEOUT_MS = 1000;
const MAX_STREAM_STARTUP_TIMEOUT_MS = 300000;

const ALLOWED_REGION_PATTERN = /^[a-z]{2}-[a-z]+-\d$/;
const ALLOWED_TEXT_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
const STREAM_GROUP_ID_PATTERN = /^sg-[a-zA-Z0-9]{5,}$/;
const APPLICATION_ID_PATTERN = /^a-[a-zA-Z0-9]{4,}$/;

export class ShellUrlConfigService {
    public static readonly DEFAULT_STREAM_STARTUP_TIMEOUT_MS = 10000;

    public static parseStreamStartupTimeoutMs(input: string | null | undefined): number | null {
        if (!input) {
            return null;
        }

        const value = Number(input.trim());
        if (!Number.isInteger(value) || value <= 0) {
            return null;
        }

        return Math.max(MIN_STREAM_STARTUP_TIMEOUT_MS, Math.min(value, MAX_STREAM_STARTUP_TIMEOUT_MS));
    }

    public parseFromWindow(locationSearch: string): ShellUrlConfig {
        const parameters = new URLSearchParams(locationSearch);

        const streamGroupId = this.validateId(parameters.get('streamGroupId') ?? '', STREAM_GROUP_ID_PATTERN);
        const applicationId = this.validateId(parameters.get('applicationId') ?? '', APPLICATION_ID_PATTERN);
        const location = this.sanitizeRegion(parameters.get('location'));
        const showStats = this.parseBoolean(parameters.get('showStats'), false);
        const streamStartupTimeoutMs = ShellUrlConfigService.parseStreamStartupTimeoutMs(parameters.get('streamStartupTimeoutMs'));
        const userId = this.sanitizeText(parameters.get('userId')) || DEFAULT_USER_ID;
        const controllerId = this.sanitizeText(parameters.get('controllerId'));

        return {
            userId,
            streamGroupId,
            applicationId,
            location,
            showStats,
            streamStartupTimeoutMs,
            controllerId,
            hasControllerId: controllerId.length > 0,
            hasRequiredStreamParams: streamGroupId.length > 0 && applicationId.length > 0,
            publisherId: this.sanitizeText(parameters.get('publisherId')),
            publisherProductId: this.sanitizeText(parameters.get('publisherProductId'))
        };
    }

    private parseBoolean(input: string | null, defaultValue: boolean): boolean {
        if (!input) {
            return defaultValue;
        }

        const value = input.trim().toLowerCase();
        if (value === 'true') {
            return true;
        }

        if (value === 'false') {
            return false;
        }

        return defaultValue;
    }

    private sanitizeRegion(input: string | null): string {
        if (!input) {
            return DEFAULT_LOCATION;
        }

        const value = input.trim().toLowerCase();
        if (!ALLOWED_REGION_PATTERN.test(value)) {
            return DEFAULT_LOCATION;
        }

        return value;
    }

    private sanitizeText(input: string | null): string {
        if (!input) {
            return '';
        }

        const trimmed = input.trim();
        if (!trimmed) {
            return '';
        }

        if (!ALLOWED_TEXT_PATTERN.test(trimmed)) {
            return '';
        }

        return trimmed;
    }

    private validateId(input: string, pattern: RegExp): string {
        const sanitized = input.replace(/[^0-9a-zA-Z-]/g, '').trim();
        if (!sanitized) {
            return '';
        }

        if (!pattern.test(sanitized)) {
            return '';
        }

        return sanitized;
    }
}
